import Phaser from "phaser";
import { currentProjection, type ScreenPos } from "@/world/map/Iso";
import type { CollisionMap } from "@/world/map/CollisionMap";
import { EdgeDir } from "@/world/map/CollisionMap";

const BLOCKED_COLOR = 0xff0000;
const BLOCKED_ALPHA = 0.3;
const EDGE_COLOR = 0xff9900;
const EDGE_LINE_WIDTH = 2.5;

/**
 * Draws a semi-transparent red iso-diamond on every blocked tile
 * and orange edge lines for directional (half-tile) blocks.
 * Toggle visibility with F3.
 */
export class CollisionDebugOverlay {
  private graphics: Phaser.GameObjects.Graphics;
  private visible = false;
  private drawnRevision = -1;
  private readonly collision: CollisionMap;
  private readonly origin: ScreenPos;

  constructor(scene: Phaser.Scene, origin: ScreenPos, collision: CollisionMap) {
    this.collision = collision;
    this.origin = origin;
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(999999);
    this.graphics.setVisible(false);

    scene.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.F3).on("down", () => {
      this.toggle();
    });
  }

  toggle(): void {
    this.visible = !this.visible;
    this.graphics.setVisible(this.visible);
    if (this.visible) this.redraw();
  }

  /** Call in update() — only redraws when collision revision changes. */
  tick(): void {
    if (!this.visible) return;
    if (this.drawnRevision !== this.collision.revision) {
      this.redraw();
    }
  }

  private redraw(): void {
    this.graphics.clear();

    const projection = currentProjection();
    const halfW = projection.tileWidth / 2;
    const halfH = projection.tileHeight / 2;
    const size = this.collision.size;

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const { sx, sy } = projection.worldToScreen(x, y);
        const cx = this.origin.sx + sx + halfW;
        const cy = this.origin.sy + sy + halfH;

        // Tile corners — iso uses diamond (top/right/bottom/left vertices),
        // top-down uses axis-aligned square (NW/NE/SE/SW corners).
        let top: Phaser.Geom.Point;
        let right: Phaser.Geom.Point;
        let bottom: Phaser.Geom.Point;
        let left: Phaser.Geom.Point;
        if (projection.kind === "iso") {
          top    = new Phaser.Geom.Point(cx, cy - halfH);
          right  = new Phaser.Geom.Point(cx + halfW, cy);
          bottom = new Phaser.Geom.Point(cx, cy + halfH);
          left   = new Phaser.Geom.Point(cx - halfW, cy);
        } else {
          top    = new Phaser.Geom.Point(cx - halfW, cy - halfH); // NW
          right  = new Phaser.Geom.Point(cx + halfW, cy - halfH); // NE
          bottom = new Phaser.Geom.Point(cx + halfW, cy + halfH); // SE
          left   = new Phaser.Geom.Point(cx - halfW, cy + halfH); // SW
        }

        // Full block overlay
        if (this.collision.isBlockedAt(x, y)) {
          this.graphics.fillStyle(BLOCKED_COLOR, BLOCKED_ALPHA);
          this.graphics.fillPoints([top, right, bottom, left], true);
        }

        // Edge flags — draw orange lines on blocked edges
        const edges = this.collision.getEdges(x, y);
        if (edges !== 0) {
          this.graphics.lineStyle(EDGE_LINE_WIDTH, EDGE_COLOR, 0.9);
          if (edges & EdgeDir.N) this.drawLine(top, right);
          if (edges & EdgeDir.E) this.drawLine(right, bottom);
          if (edges & EdgeDir.S) this.drawLine(bottom, left);
          if (edges & EdgeDir.W) this.drawLine(left, top);
        }
      }
    }
    this.drawnRevision = this.collision.revision;
  }

  private drawLine(a: Phaser.Geom.Point, b: Phaser.Geom.Point): void {
    this.graphics.beginPath();
    this.graphics.moveTo(a.x, a.y);
    this.graphics.lineTo(b.x, b.y);
    this.graphics.strokePath();
  }

  destroy(): void {
    this.graphics.destroy();
  }
}
